import { TemplateModel } from '../TemplateModel';
import { TemplateBlock } from '../Blocks/BaseBlock';
import { typeIconSVG as textFieldIcon } from '../Blocks/TextField/TextFieldRender';
import { typeIconSVG as headingIcon } from '../Blocks/Heading/HeadingRender';
import { typeIconSVG as subheadingIcon } from '../Blocks/Subheading/SubheadingRender';
import { typeIconSVG as multipleChoiceIcon } from '../Blocks/MultipleChoice/MultipleChoiceRender';
import { typeIconSVG as checkboxesIcon } from '../Blocks/Checkboxes/CheckboxesRender';
import { typeIconSVG as tableIcon } from '../Blocks/Table/TableRender';

const DISPLAY_ONLY_KEYS = [
  'icon',
  'title',
  'instructions',
  'linkToBestPractices',
  'autoDocExtField',
];

const BLOCK_TYPE_DEFAULTS: Record<string, Record<string, unknown>> = {
  TextField: { icon: textFieldIcon, title: 'Text field' },
  Heading: { icon: headingIcon, title: 'Heading' },
  Subheading: { icon: subheadingIcon, title: 'Subheading' },
  MultipleChoice: { icon: multipleChoiceIcon, title: 'Single choice' },
  Checkboxes: { icon: checkboxesIcon, title: 'Checkboxes' },
  CustomTable: {
    icon: tableIcon,
    title: 'Table',
    instructions: [
      'Use clear, descriptive column and row labels so reviewers can extract data consistently and avoid consensus issues.',
      'You can always add more columns or rows as needed during extraction.',
    ],
    linkToBestPractices:
      'https://support.covidence.org/help/best-practice-for-tables-in-extraction-2',
  },
};

function hydrateBlock(block: TemplateBlock): TemplateBlock {
  const defaults = BLOCK_TYPE_DEFAULTS[block.type];
  if (!defaults) return block;
  return { ...defaults, ...block };
}

export { hydrateBlock };

function dehydrateBlock(block: TemplateBlock): TemplateBlock {
  return Object.fromEntries(
    Object.entries(block).filter(([key]) => !DISPLAY_ONLY_KEYS.includes(key))
  ) as TemplateBlock;
}

export type TemplateViewModel = {
  blocks: TemplateBlock[];
};

export function fromModel(model: TemplateModel): TemplateViewModel {
  return { blocks: model.blocks.map(hydrateBlock) };
}

export function toModel(viewModel: TemplateViewModel): TemplateModel {
  return { blocks: viewModel.blocks.map(dehydrateBlock) };
}

export function create(): TemplateViewModel {
  return {
    blocks: [],
  };
}

export function build(blocks: TemplateBlock[]) {
  return {
    blocks,
  };
}

export function add(
  viewModel: TemplateViewModel,
  block: TemplateBlock,
  afterID: string | null
): TemplateViewModel {
  const blocks = [...viewModel.blocks];

  if (afterID === null) {
    blocks.unshift(block);
  } else {
    const index = blocks.findIndex((block) => block.id === afterID);

    if (index === -1) {
      throw new Error(`Cannot add block, invalid block id: ${afterID}`);
    }

    blocks.splice(index + 1, 0, block);
  }

  return { ...viewModel, blocks };
}

export function remove(viewModel: TemplateViewModel, id: string) {
  const blocks = [...viewModel.blocks];
  const index = blocks.findIndex((block) => block.id === id);

  if (index === -1) {
    throw new Error(`Cannot remove block, invalid block id: ${id}`);
  }

  blocks.splice(index, 1);
  return { ...viewModel, blocks };
}

export function move(
  viewModel: TemplateViewModel,
  id: string,
  toIndex: number | null
): TemplateViewModel {
  const blocks = [...viewModel.blocks];

  const movedIndex = blocks.findIndex((block) => block.id === id);
  if (movedIndex === -1) {
    throw new Error(`Cannot move block, invalid block id: ${id}`);
  }

  const [moved] = blocks.splice(movedIndex, 1);

  if (toIndex === null) {
    blocks.unshift(moved);
  } else {
    if (toIndex < 0 || toIndex > blocks.length) {
      throw new Error(`Cannot move block, invalid index: ${toIndex}`);
    }

    blocks.splice(toIndex, 0, moved);
  }

  return { ...viewModel, blocks };
}

export function replace(
  viewModel: TemplateViewModel,
  indexOrSet: number | 'all',
  newBlock: TemplateBlock
) {
  let index: number;
  let deleteCount = 1;
  if (indexOrSet === 'all') {
    index = 0;
    deleteCount = viewModel.blocks.length;
  } else {
    index = indexOrSet;
    if (index < 0 || index >= viewModel.blocks.length) {
      throw new Error(`Cannot replace block, invalid index: ${index}`);
    }
  }

  const blocks = [...viewModel.blocks];
  blocks.splice(index, deleteCount, newBlock);
  return { ...viewModel, blocks };
}

export function updateBlock(
  viewModel: TemplateViewModel,
  newBlock: TemplateBlock
) {
  return {
    ...viewModel,
    blocks: viewModel.blocks.map((block) =>
      block.id === newBlock.id ? newBlock : block
    ),
  };
}
